import { FunctionComponent } from "react";

import { FindPersonMode } from "@signalist/shared-types";

interface ModeCardProps {
	selected: boolean;
	title: string;
	description: string;
	badge?: string;
	testId: string;
	onSelect: () => void;
}

/** One radio card of a Find person choice; shared by the two selectors of the step. */
export const ModeCard: FunctionComponent<ModeCardProps> = ({
	selected,
	title,
	description,
	badge,
	testId,
	onSelect,
}) => (
	<button
		aria-checked={selected}
		className={`flex items-start gap-3 rounded-lg border p-4 text-left transition-colors cursor-pointer ${
			selected ? "border-brand-light bg-brand-white" : "border-border-light bg-white"
		}`}
		data-testid={testId}
		role="radio"
		type="button"
		onClick={onSelect}
	>
		<span
			className={`mt-0.5 flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full border ${
				selected ? "border-brand-light" : "border-border"
			}`}
		>
			{selected && <span className="h-2.5 w-2.5 rounded-full bg-brand-light" />}
		</span>
		<span className="flex flex-col gap-1.5">
			<span className="flex items-center gap-2 text-[15px] font-bold text-text">
				{title}
				{badge && (
					<span className="rounded-full bg-border-light px-2 py-0.5 text-[11px] font-normal text-gray-700">
						{badge}
					</span>
				)}
			</span>
			<span className="text-[13px] leading-5 text-gray-700">{description}</span>
		</span>
	</button>
);

interface FindPersonModeSelectorProps {
	value: FindPersonMode;
	onChange: (mode: FindPersonMode) => void;
	/** Shown when the list already holds companies (editing the mode later, screen D). */
	existingCompaniesNote?: string;
}

/**
 * The "When should we search for people?" choice of a company-based list's Find person step
 * (PREC-4400): find people now (today's behaviour) vs enrich companies only and find people later.
 */
export const FindPersonModeSelector: FunctionComponent<FindPersonModeSelectorProps> = ({
	value,
	onChange,
	existingCompaniesNote,
}) => (
	<section
		aria-label="When should we search for people?"
		className="mb-5 flex flex-col gap-3.5 rounded-xl border border-border-light p-5"
	>
		<div>
			<h2 className="text-base font-bold text-text">When should we search for people?</h2>
			<div className="mt-1 text-xs text-gray-700">
				You can change this later from the list&apos;s Find Person page or List Settings.
			</div>
		</div>
		<div className="grid grid-cols-1 gap-3 sm:grid-cols-2" role="radiogroup">
			<ModeCard
				badge="Default"
				description="Each qualifying company is searched with the profiles below as soon as it is added."
				selected={value === FindPersonMode.FIND_NOW}
				testId="find-person-mode-find-now"
				title="Find people now"
				onSelect={() => onChange(FindPersonMode.FIND_NOW)}
			/>
			<ModeCard
				description="Companies are enriched, qualified and scored. Nobody is searched yet. The companies appear on the list's Companies tab; select the ones you want and use Enrich → Find person."
				selected={value === FindPersonMode.COMPANY_ONLY}
				testId="find-person-mode-company-only"
				title="Only enrich companies, find people later"
				onSelect={() => onChange(FindPersonMode.COMPANY_ONLY)}
			/>
		</div>
		{existingCompaniesNote && (
			<div
				className="flex items-start gap-2.5 rounded-lg border border-tag-warning-border bg-tag-warning-light p-3 text-[13px] leading-5 text-tag-warning-dark"
				role="note"
			>
				{existingCompaniesNote}
			</div>
		)}
	</section>
);
